import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import { Marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import hljs from 'highlight.js'

interface StreamingMarkdownOptions {
  langPrefix?: string
  highlight?: (code: string, lang: string) => string
}

// Patterns for detecting incomplete markdown blocks
const INCOMPLETE_PATTERNS = {
  codeBlock: /```\w{0,20}\n?([^`]*)$/,
  inlineCode: /`([^`]*)$/,
  bold: /\*\*([^*]*)$/,
  italic: /\*([^*]*)$/,
  strikethrough: /~~([^~]*)$/,
  link: /\[([^\]]*)\]\(([^)]*)$/,
  heading: /^#{1,6} (.*)$/m,
  table: /\|[^|\n]*\|?$/,
  listItem: /^(\s{0,10}[-*+]|\s{0,10}\d{1,3}\.) (.*)$/m,
}

export function useStreamingMarkdown(options: StreamingMarkdownOptions = {}) {
  const marked = new Marked(
    markedHighlight({
      langPrefix: options.langPrefix || 'hljs language-',
      highlight: options.highlight || ((code, lang) => {
        const language = hljs.getLanguage(lang) ? lang : 'plaintext'
        return hljs.highlight(code, { language }).value
      }),
    }),
  )

  function isIncompleteBlock(content: string): boolean {
    // Check for incomplete code blocks
    const codeBlockMatches = content.match(/```/g)
    if (codeBlockMatches && codeBlockMatches.length % 2 === 1)
      return true

    // Check for incomplete inline elements at the end
    const lastLine = content.split('\n').pop()
      || ''

    return (
      INCOMPLETE_PATTERNS.inlineCode.test(lastLine)
      || INCOMPLETE_PATTERNS.bold.test(lastLine)
      || INCOMPLETE_PATTERNS.italic.test(lastLine)
      || INCOMPLETE_PATTERNS.strikethrough.test(lastLine)
      || INCOMPLETE_PATTERNS.link.test(lastLine)
    )
  }

  function parseStreamingMarkdown(content: string): string {
    if (!content)
      return ''

    let processedContent = content

    // Handle incomplete code blocks by temporarily closing them
    const codeBlockMatches = content.match(/```/g)
    if (codeBlockMatches && codeBlockMatches.length % 2 === 1) {
      // Find the last incomplete code block
      const lastCodeBlockIndex = content.lastIndexOf('```')
      const beforeCodeBlock = content.substring(0, lastCodeBlockIndex)
      const codeBlockContent = content.substring(lastCodeBlockIndex + 3)

      // Temporarily close the code block for parsing
      processedContent = `${beforeCodeBlock}\`\`\`${codeBlockContent}\n\`\`\``
    }

    // Handle incomplete inline code
    processedContent = processedContent.replace(INCOMPLETE_PATTERNS.inlineCode, (_match, code) => {
      return `\`${code}\``
    })

    // Handle incomplete bold text
    processedContent = processedContent.replace(INCOMPLETE_PATTERNS.bold, (_match, text) => {
      return `**${text}**`
    })

    // Handle incomplete italic text
    processedContent = processedContent.replace(INCOMPLETE_PATTERNS.italic, (match, text) => {
      // Don't match if it's part of bold text
      if (!match.includes('**'))
        return `*${text}*`

      return match
    })

    // Handle incomplete strikethrough
    processedContent = processedContent.replace(INCOMPLETE_PATTERNS.strikethrough, (_match, text) => {
      return `~~${text}~~`
    })

    // Handle incomplete links
    processedContent = processedContent.replace(INCOMPLETE_PATTERNS.link, (_match, linkText, url) => {
      return `[${linkText}](${url || '#'})`
    })

    try {
      return marked.parse(processedContent) as string
    }
    catch (error) {
      console.warn('Markdown parsing error:', error)
      // Fallback to escaped HTML
      return processedContent
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/\n/g, '<br>')
    }
  }

  function createStreamingParser(streamingContent: Ref<string>) {
    return computed(() => parseStreamingMarkdown(streamingContent.value))
  }

  return {
    parseStreamingMarkdown,
    createStreamingParser,
    isIncompleteBlock,
  }
}

// Composable for handling streaming markdown in components
export function useStreamingMessage() {
  const content = ref('')
  const isStreaming = ref(false)
  const { createStreamingParser } = useStreamingMarkdown()

  const parsedContent = createStreamingParser(content)

  function appendContent(newContent: string) {
    content.value += newContent
  }

  function setContent(newContent: string) {
    content.value = newContent
  }

  function startStreaming() {
    isStreaming.value = true
    content.value = ''
  }

  function stopStreaming() {
    isStreaming.value = false
  }

  function reset() {
    content.value = ''
    isStreaming.value = false
  }

  return {
    content,
    parsedContent,
    isStreaming,
    appendContent,
    setContent,
    startStreaming,
    stopStreaming,
    reset,
  }
}
